import { useCallback, useContext, useMemo } from "react";

// context
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// types
import { COUPON_GROUP_TYPES } from "@swiggy-private/cart-types";
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";
import { CouponInfo } from "@swiggy-private/core-ui";
import { CheckoutContext, CouponPageType } from "@swiggy-private/checkout-core";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { CouponAnalytics } from "../types";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { getRestaurantId } from "@cart-client/helpers/Cart";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";

interface CouponHandlersProps {
    croutonTitle: string;
    couponListingType: CouponPageType;
    isPaymentCouponListingPage: React.MutableRefObject<boolean>;
    cartResponse: ReviewedCart | null;
    paymentOffersRef: React.RefObject<HTMLDivElement>;
    setShowCrouton: (show: boolean) => void;
    analytics: CouponAnalytics;
}

export const useCouponHandlers = ({
    croutonTitle,
    couponListingType,
    isPaymentCouponListingPage,
    cartResponse,
    paymentOffersRef,
    setShowCrouton,
    analytics,
}: CouponHandlersProps) => {
    const { checkoutState } = useContext(CheckoutContext);
    const { data: cartFlowData } = useContext(CartFlowContext);
    const isMROCart = isMROFlow(cartFlowData?.cartSource);
    const { syncApplyCouponCart, handleCouponListingPageCouponRemove } = useContext(UpdateCartContext);
    const { setTncCoupon, clearErrorMessage, errorMessage } = useContext(CouponListingContext);
    const restaurantId = cartResponse ? getRestaurantId(cartResponse) : "";

    const applyCouponRxId = useMemo(() => {
        if (isMROCart && !isEmpty(checkoutState.payload?.mroCouponListingRestaurantId) && !isEmpty(cartResponse)) {
            return String(checkoutState.payload?.mroCouponListingRestaurantId || "");
        }
        return;
    }, [cartResponse, checkoutState.payload?.mroCouponListingRestaurantId, isMROCart]);

    const applyCouponHandler = useCallback(
        ({ couponCode = "", offerId, isApplicable, type, timerPayload, couponType }: CouponCardImpressionPayload) => {
            if (offerId && isApplicable) {
                analytics.handleClickCouponOffers({
                    couponCode,
                    couponType: couponType || "",
                    offerId,
                    isApplicable,
                    timerPayload,
                    storeId: applyCouponRxId || restaurantId,
                });
            } else {
                analytics.handleClickApplyCoupon(couponCode, applyCouponRxId);
            }

            if (type === COUPON_GROUP_TYPES.APPLIED_OFFERS) {
                analytics.handleClickRemoveCoupon(couponCode, couponType || "");
                handleCouponListingPageCouponRemove({
                    subpage: couponListingType,
                    couponCode,
                    restaurantId: applyCouponRxId,
                });
            } else {
                syncApplyCouponCart({
                    couponCode,
                    isCouponListingFlow: true,
                    isPaymentCouponListingFlow: isPaymentCouponListingPage.current,
                    restaurantId: applyCouponRxId,
                });
            }
        },
        [
            analytics,
            restaurantId,
            handleCouponListingPageCouponRemove,
            couponListingType,
            syncApplyCouponCart,
            isPaymentCouponListingPage,
            applyCouponRxId,
        ],
    );

    const closeTncCouponHalfcard = useCallback(() => {
        setTncCoupon?.(null);
    }, [setTncCoupon]);

    const closeBottomSheet = useCallback(() => {
        if (!isEmpty(errorMessage)) {
            analytics.handleClickHalfCardInvalidCoupon(errorMessage);
        }
        clearErrorMessage();
    }, [clearErrorMessage, errorMessage, analytics]);

    const showTncCouponHalfcard = useCallback(
        (coupon: CouponInfo) => {
            setTncCoupon?.(coupon);
        },
        [setTncCoupon],
    );

    const onViewEnter = useCallback(() => {
        setShowCrouton(false);
    }, [setShowCrouton]);

    const onCroutonOfferClick = useCallback(() => {
        analytics.handleClickOfferCrouton(croutonTitle, cartResponse);
        paymentOffersRef.current?.scrollIntoView({ behavior: "smooth" });
        setShowCrouton(false);
    }, [analytics, croutonTitle, cartResponse, paymentOffersRef, setShowCrouton]);

    return {
        applyCouponHandler,
        closeTncCouponHalfcard,
        closeBottomSheet,
        showTncCouponHalfcard,
        onViewEnter,
        onCroutonOfferClick,
    };
};
